import { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CSSObject } from 'styled-components';
import tw from 'twin.macro';

import { SwapIcon } from '@icons/SwapIcon';
import { Button } from '@components/Button';
import { Loader } from '@components/Loader';
import { useMegaphoneNetworks } from '@account_module/hooks/useMegaphoneNetworks';
import { MegaphoneNetworkType } from '@account_module/types/Onboarding';
import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { useIntegration } from '@account_module/hooks/useIntegrations';
import { IntegrationParamsType } from '@account_module/types/Integration';
import { getIdsPayloadString } from '@account_module/utils/integrationUtils';
import loaderImage from '@assets/images/loader.png';

const transitionDuration = 200 as number;

type ListsStyleType = {
  include: CSSObject;
  exclude: CSSObject;
};

const initialListStyles: ListsStyleType = {
  include: {
    marginLeft: '0',
    transition: 'none',
  },
  exclude: {
    marginLeft: '50%',
    transition: 'none',
  },
};

const FlexBetween = tw.div`flex items-center justify-between`;
const Container = tw.div`h-full p-[24px] flex flex-col gap-[24px]`;
const LoaderContainer = tw.div`w-full h-full flex flex-col items-center justify-center`;
const LoaderText = tw.span`text-primary text-[24px] font-bold mt-[32px]`;
const Title = tw.h3`text-left text-[24px] mb-[12px]`;
const Subtitle = tw.p`text-left text-base leading-[150%] text-text-muted-2 max-w-[747px]`;
const ListWrapper = tw.div`h-full border border-solid border-card-border rounded-lg overflow-hidden`;
const ListContainer = tw.div`h-full flex items-start justify-start overflow-hidden relative bg-white`;
const ListHeader = tw.div`bg-[#EAECFB] p-[16px]`;
const ListInfoHeader = tw.div`flex items-center justify-between px-4 py-[6px] bg-selected border-b border-solid border-card-border`;
const List = tw.div`h-full w-1/2 overflow-y-scroll absolute`;
const ListTitle = tw.span`text-[#192576] text-base font-semibold leading-[150%]`;
const ListItem = tw(
  FlexBetween
)`py-[12px] px-[16px] border-b border-solid border-card-border bg-white hover:bg-selected`;
const ItemText = tw.span`text-[14px] text-primary whitespace-nowrap truncate`;
const ItemTextSmall = tw.span`text-[13px] text-text-muted-2 whitespace-nowrap`;
const InviteUser = tw.p`text-left text-text-muted-2 text-sm leading-[150%] cursor-pointer`;
const ActionButton = tw.button`border border-solid border-card-border text-accent py-[5px] px-[10px] shadow-ui rounded text-[13px] font-semibold`;
const SwapListButton = tw.button`hover:bg-muted4 mx-[32px] p-1 rounded`;
const StyledSpan = tw.span`text-[14px] text-text-muted-2 font-bold leading-[150%]`;
const ExpandableDescription = tw.div`block text-[#192576] mt-0 overflow-hidden p-0 h-auto transition-all h-[50px] duration-200`;
const NextButton = tw(
  Button.Primary
)`px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center w-[84px] min-w-[84px] bg-[#2A3FCB]`;

const BackButton = tw(
  NextButton
)`text-primary border border-solid border-[rgba(42, 63, 203, 0.50)] bg-transparent`;
const ReadMoreButton = tw.button`text-base text-[#4E60DA] underline`;

const LoadingSpinner = () => (
  <img tw='animate-spin' width='73' height='73' src={loaderImage} alt='loader' />
);

type CtaType = 'next' | 'back';

const CTA_TYPE = {
  NEXT: 'next' as CtaType,
  BACK: 'back' as CtaType,
};

export const CustomizeImportList = ({
  onSuccess,
  onGoBack,
}: {
  onSuccess: () => void;
  onGoBack: () => void;
}) => {
  const { t } = useTranslation();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
  const [selectedCTA, setSelectedCTA] = useState<CtaType>(CTA_TYPE.NEXT);
  const {
    integrationId,
    networksToExclude,
    networksToInclude,
    setNetworksToExclude,
    setNetworksToInclude,
    setIsInviteModalVisible,
    swapLists,
    organizationId,
    token,
  } = useMegaphoneOnboardContext();
  const { data, error, isLoading, networksIds, refetch } = useMegaphoneNetworks(
    organizationId,
    token
  );
  const { updateIntegration, isUpdateLoading } = useIntegration(integrationId);

  const [listStyle, setListStyle] = useState<ListsStyleType>(initialListStyles);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const inludeNetworksList = useMemo(
    () => data?.filter((item) => networksToInclude.includes(item?.id)) || [],
    [data]
  );
  const excludeNetworksList = useMemo(
    () => data?.filter((item) => networksToExclude.includes(item?.id)) || [],
    [data]
  );

  const handleSwapLists = () => {
    setListStyle({
      include: {
        marginLeft: '50%',
        transition: `margin ${transitionDuration}ms ease-in`,
      },
      exclude: {
        marginLeft: '0',
        transition: `margin ${transitionDuration}ms ease-in`,
      },
    });

    setTimeout(() => {
      setListStyle(initialListStyles);
      swapLists();
    }, transitionDuration);
  };

  const submitData = async () => {
    setIsSubmiting(true);

    if (integrationId && updateIntegration) {
      const requestData: IntegrationParamsType = {
        include_networks: getIdsPayloadString(networksToInclude),
        exclude_networks: getIdsPayloadString(networksToExclude),
        progress_step: 2,
      };
      await updateIntegration(requestData);
      onSuccess();
    }
  };

  const handleGoBack = async () => {
    if (updateIntegration) {
      const data = await updateIntegration({ progress_step: 0 } as IntegrationParamsType);

      if (data) {
        onGoBack();
      }
    }
  };

  const reloadPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (networksIds?.length && (!networksToExclude.length || !networksToInclude.length)) {
      networksIds.forEach((element) => {
        setNetworksToInclude(element);
      });
    }
  }, [networksIds]);

  useEffect(() => {
    refetch();
  }, [organizationId]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <LoadingSpinner />
        <LoaderText>
          {t('account_module.onboard_page.customize_import_list.loading_copy')}
        </LoaderText>
      </LoaderContainer>
    );
  }

  if (error)
    return (
      <Container tw='relative'>
        <div tw='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
          <Title tw='text-center'>
            {t('account_module.onboard_page.customize_import_list.error.title')}
          </Title>
          <Subtitle tw='text-center mb-4'>
            {t('account_module.onboard_page.customize_import_list.error.message')}
          </Subtitle>
          <Button.Primary tw='mx-auto' onClick={reloadPage}>
            {t('account_module.onboard_page.customize_import_list.error.action')}
          </Button.Primary>
        </div>
      </Container>
    );

  if (!isLoading && !data?.length) return <Container>No data</Container>;

  return (
    <Container>
      <div>
        <Title>{t('account_module.onboard_page.customize_import_list.title')}</Title>
        <Subtitle>{t('account_module.onboard_page.customize_import_list.subtitle')}</Subtitle>
      </div>

      <ListWrapper>
        <ListHeader>
          <FlexBetween>
            <ListTitle>
              {t('account_module.onboard_page.customize_import_list.description')}
            </ListTitle>
            <ReadMoreButton onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
              {isDescriptionExpanded ? t('common.read_less') : t('common.read_more')}
            </ReadMoreButton>
          </FlexBetween>
          <ExpandableDescription
            css={[!isDescriptionExpanded ? tw`max-h-[0]` : tw`max-h-[50px] mt-2`]}>
            {t('account_module.onboard_page.customize_import_list.description_2')}
          </ExpandableDescription>
        </ListHeader>
        <ListInfoHeader>
          <FlexBetween tw='w-full'>
            <StyledSpan>
              {t('account_module.onboard_page.customize_import_list.include_title')}
            </StyledSpan>
            <StyledSpan tw='text-sm font-semibold leading-[140%]'>
              {t('account_module.onboard_page.customize_import_list.networks_count', {
                count: inludeNetworksList.length,
              })}
            </StyledSpan>
          </FlexBetween>
          <SwapListButton onClick={handleSwapLists}>
            <SwapIcon tw='fill-[#414753]' />
          </SwapListButton>
          <FlexBetween tw='w-full'>
            <StyledSpan>
              {t('account_module.onboard_page.customize_import_list.exclude_title')}
            </StyledSpan>
            <StyledSpan tw='text-sm font-semibold'>
              {t('account_module.onboard_page.customize_import_list.networks_count', {
                count: excludeNetworksList.length,
              })}
            </StyledSpan>
          </FlexBetween>
        </ListInfoHeader>
        <ListContainer
          css={[isDescriptionExpanded ? tw`h-[calc(100% - 157px)]` : tw`h-[calc(100% - 107px)]`]}>
          <List tw='border-r border-solid border-card-border' style={listStyle.include}>
            {inludeNetworksList?.map((item: MegaphoneNetworkType | undefined) => (
              <ListItem key={item?.id}>
                <ItemText>{item?.name}</ItemText>
                <FlexBetween tw='justify-end gap-[12px]'>
                  <ItemTextSmall>
                    {t('account_module.onboard_page.customize_import_list.shows_count', {
                      count: item?.podcastCount,
                    })}
                  </ItemTextSmall>
                  <ActionButton
                    onClick={() => {
                      if (item?.id) {
                        setNetworksToExclude(item.id);
                      }
                    }}>
                    {t('account_module.onboard_page.customize_import_list.action.exclude')}
                  </ActionButton>
                </FlexBetween>
              </ListItem>
            ))}
          </List>
          <List style={listStyle.exclude}>
            {excludeNetworksList?.map((item: MegaphoneNetworkType | undefined) => (
              <ListItem key={item?.id}>
                <ItemText>{item?.name}</ItemText>
                <FlexBetween tw='justify-end gap-[12px]'>
                  <ItemTextSmall>
                    {t('account_module.onboard_page.customize_import_list.shows_count', {
                      count: item?.podcastCount,
                    })}
                  </ItemTextSmall>
                  <ActionButton
                    onClick={() => {
                      if (item?.id) {
                        setNetworksToInclude(item.id);
                      }
                    }}>
                    {t('account_module.onboard_page.customize_import_list.action.include')}
                  </ActionButton>
                </FlexBetween>
              </ListItem>
            ))}
          </List>
        </ListContainer>
      </ListWrapper>

      <FlexBetween tw='px-4'>
        <InviteUser onClick={() => setIsInviteModalVisible(true)}>
          <Trans i18nKey='account_module.onboard_page.credentials_form.invite_coworker_link'>
            <span tw='text-accent underline'>Invite a team member</span> to help you with
            onboarding.
          </Trans>
        </InviteUser>
        <FlexBetween tw='gap-4'>
          {!networksToInclude.length && (
            <StyledSpan tw='font-semibold'>
              {t('account_module.onboard_page.customize_import_list.no_networks_included_warning')}
            </StyledSpan>
          )}
          <BackButton
            disabled={!networksToInclude.length || isUpdateLoading}
            onClick={() => {
              setSelectedCTA(CTA_TYPE.BACK);
              handleGoBack();
            }}>
            {isUpdateLoading && selectedCTA === CTA_TYPE.BACK ? (
              <Loader size='sm' theme='light' />
            ) : (
              t('common.back')
            )}
          </BackButton>
          <NextButton
            onClick={() => {
              setSelectedCTA(CTA_TYPE.NEXT);
              submitData();
            }}
            disabled={!networksToInclude.length || isUpdateLoading}>
            {(isSubmiting || isUpdateLoading) && selectedCTA === CTA_TYPE.NEXT ? (
              <Loader size='sm' theme='dark' />
            ) : (
              t('common.next')
            )}
          </NextButton>
        </FlexBetween>
      </FlexBetween>
    </Container>
  );
};
